/*
=========================================
QUESS TRAINING CART DETECTOR
=========================================

This script:

- Detects training products in the cart
- Reads each variant's LMS course ID
- Calculates purchased training seats
- Refreshes whenever the Shopify cart changes
- Makes the result available to the future
  registration-form script
*/

(function () {
  "use strict";


  if (
    window.__quessTrainingCartDetectorLoaded
  ) {
    return;
  }


  window.__quessTrainingCartDetectorLoaded =
    true;


  const SECTION_ID =
    "training-cart-data";


  const CART_MUTATION_PATTERN =
    /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/;


  const shopifyRoot =
    (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    )
      ? window.Shopify.routes.root
      : "/";


  /*
  Capture the current fetch function.

  This allows the detector to coexist with
  the existing wipe-kit-pairing.js script.
  */

  const previousFetch =
    window.fetch.bind(window);


  let refreshTimer = null;
  let refreshInFlight = null;
  let refreshAgain = false;


  function getTrainingSectionUrl() {
    const normalizedRoot =
      shopifyRoot.endsWith("/")
        ? shopifyRoot
        : `${shopifyRoot}/`;


    const url =
      new URL(
        `${normalizedRoot}cart`,
        window.location.origin
      );


    url.searchParams.set(
      "section_id",
      SECTION_ID
    );


    return url.toString();
  }


  function normalizeTrainingItems(
    payload
  ) {
    if (
      !payload ||
      !Array.isArray(
        payload.training_items
      )
    ) {
      return [];
    }


    return payload.training_items
      .map(
        function (
          item
        ) {
          return {
            lineKey:
              String(
                item.line_key || ""
              ),

            lineIndex:
              Number(
                item.line_index || 0
              ),

            productId:
              String(
                item.product_id || ""
              ),

            variantId:
              String(
                item.variant_id || ""
              ),

            productHandle:
              String(
                item.product_handle || ""
              ),

            productTitle:
              String(
                item.product_title || ""
              ),

            variantTitle:
              String(
                item.variant_title || ""
              ),

            quantity:
              Math.max(
                0,
                Number(
                  item.quantity || 0
                )
              ),

            lmsCourseId:
              String(
                item.lms_course_id || ""
              ).trim(),

            url:
              String(
                item.url || ""
              ),

            finalLinePrice:
              Math.max(
                0,
                Number(
                  item.final_line_price || 0
                )
              )
          };
        }
      )
      .filter(
        function (
          item
        ) {
          return (
            item.variantId &&
            item.quantity > 0
          );
        }
      );
  }


  function buildTrainingCartState(
    payload
  ) {
    const trainingItems =
      normalizeTrainingItems(
        payload
      );


    const totalTrainingSeats =
      trainingItems.reduce(
        function (
          total,
          item
        ) {
          return (
            total +
            item.quantity
          );
        },
        0
      );


    const minimumLearnerCount =
      trainingItems.reduce(
        function (
          highestQuantity,
          item
        ) {
          return Math.max(
            highestQuantity,
            item.quantity
          );
        },
        0
      );


    const missingCourseItems =
      trainingItems.filter(
        function (
          item
        ) {
          return !item.lmsCourseId;
        }
      );


    return {
      ready: true,

      updatedAt:
        new Date().toISOString(),

      cartToken:
        String(
          payload.cart_token || ""
        ),

      cartItemCount:
        Number(
          payload.cart_item_count || 0
        ),

      hasTraining:
        trainingItems.length > 0,

      trainingCourseCount:
        trainingItems.length,

      totalTrainingSeats,

      /*
      At least this many learners are
      required because one learner cannot
      receive the same course twice.
      */

      minimumLearnerCount,

      /*
      This assumes every learner must
      receive at least one purchased seat.
      */

      maximumLearnerCount:
        totalTrainingSeats,

      hasMissingCourseIds:
        missingCourseItems.length > 0,

      missingCourseItems,

      trainingItems
    };
  }


  function publishTrainingCartState(
    state
  ) {
    window.QuessTrainingCart =
      state;


    document.documentElement
      .classList.toggle(
        "quess-cart-has-training",
        state.hasTraining
      );


    document.documentElement
      .classList.toggle(
        "quess-cart-has-no-training",
        !state.hasTraining
      );


    document.documentElement.setAttribute(
      "data-quess-training-courses",
      String(
        state.trainingCourseCount
      )
    );


    document.documentElement.setAttribute(
      "data-quess-training-seats",
      String(
        state.totalTrainingSeats
      )
    );


    /*
    The registration form will listen
    for this event in the next step.
    */

    document.dispatchEvent(
      new CustomEvent(
        "quess:training-cart-updated",
        {
          detail:
            state
        }
      )
    );


    return state;
  }


  function publishTrainingCartError(
    error
  ) {
    const message =
      error instanceof Error
        ? error.message
        : "Unknown training cart error";


    console.error(
      "QUESS training cart detector error:",
      error
    );


    const previousState =
      window.QuessTrainingCart &&
      typeof window.QuessTrainingCart ===
        "object"
        ? window.QuessTrainingCart
        : {};


    window.QuessTrainingCart = {
      ...previousState,

      ready: false,

      error:
        message,

      updatedAt:
        new Date().toISOString()
    };


    document.dispatchEvent(
      new CustomEvent(
        "quess:training-cart-error",
        {
          detail: {
            message
          }
        }
      )
    );
  }


  async function refreshTrainingCartData() {
    if (refreshInFlight) {
      refreshAgain = true;

      return refreshInFlight;
    }


    refreshInFlight =
      (
        async function () {
          const response =
            await previousFetch(
              getTrainingSectionUrl(),
              {
                method: "GET",

                credentials:
                  "same-origin",

                cache:
                  "no-store",

                headers: {
                  "Accept":
                    "text/html",

                  "X-Requested-With":
                    "XMLHttpRequest"
                }
              }
            );


          if (!response.ok) {
            throw new Error(
              `Training cart section returned HTTP ${response.status}.`
            );
          }


          const sectionHtml =
            await response.text();


          const documentParser =
            new DOMParser();


          const sectionDocument =
            documentParser.parseFromString(
              sectionHtml,
              "text/html"
            );


          const dataElement =
            sectionDocument.querySelector(
              "[data-quess-training-cart-data]"
            );


          if (!dataElement) {
            throw new Error(
              "The training cart data element could not be found."
            );
          }


          let payload;


          try {
            payload =
              JSON.parse(
                dataElement.textContent ||
                "{}"
              );
          }


          catch (error) {
            throw new Error(
              "The training cart data returned invalid JSON."
            );
          }


          const state =
            buildTrainingCartState(
              payload
            );


          return publishTrainingCartState(
            state
          );
        }
      )();


    try {
      return await refreshInFlight;
    }


    catch (error) {
      publishTrainingCartError(
        error
      );

      return null;
    }


    finally {
      refreshInFlight =
        null;


      if (refreshAgain) {
        refreshAgain =
          false;

        scheduleTrainingCartRefresh(
          0
        );
      }
    }
  }


  function scheduleTrainingCartRefresh(
    delay
  ) {
    window.clearTimeout(
      refreshTimer
    );


    refreshTimer =
      window.setTimeout(
        function () {
          refreshTrainingCartData();
        },
        Number.isFinite(delay)
          ? delay
          : 100
      );
  }


  function getRequestUrl(
    request
  ) {
    if (
      typeof request ===
        "string"
    ) {
      return request;
    }


    if (
      request instanceof Request
    ) {
      return request.url;
    }


    if (
      request &&
      typeof request.url ===
        "string"
    ) {
      return request.url;
    }


    return "";
  }


  function getRequestMethod(
    request,
    options
  ) {
    if (
      options &&
      typeof options.method ===
        "string"
    ) {
      return options.method.toUpperCase();
    }


    if (
      request instanceof Request
    ) {
      return request.method.toUpperCase();
    }


    return "GET";
  }


  function isCartMutationRequest(
    request,
    options
  ) {
    try {
      const requestUrl =
        getRequestUrl(
          request
        );


      const requestMethod =
        getRequestMethod(
          request,
          options
        );


      if (
        !requestUrl ||
        requestMethod === "GET"
      ) {
        return false;
      }


      const pathname =
        new URL(
          requestUrl,
          window.location.origin
        ).pathname;


      return CART_MUTATION_PATTERN.test(
        pathname
      );
    }


    catch (error) {
      return false;
    }
  }


  /*
  Watch fetch-based Shopify cart changes.
  */

  window.fetch =
    async function (
      ...argumentsList
    ) {
      const response =
        await previousFetch(
          ...argumentsList
        );


      if (
        isCartMutationRequest(
          argumentsList[0],
          argumentsList[1]
        )
      ) {
        scheduleTrainingCartRefresh(
          80
        );


        /*
        A second refresh helps themes whose
        visual cart update completes slightly
        after the request resolves.
        */

        window.setTimeout(
          function () {
            scheduleTrainingCartRefresh(
              0
            );
          },
          500
        );
      }


      return response;
    };


  /*
  Watch older themes that use
  XMLHttpRequest instead of fetch.
  */

  const originalXhrOpen =
    XMLHttpRequest.prototype.open;


  const originalXhrSend =
    XMLHttpRequest.prototype.send;


  XMLHttpRequest.prototype.open =
    function (
      method,
      url,
      ...remainingArguments
    ) {
      try {
        const pathname =
          new URL(
            String(url),
            window.location.origin
          ).pathname;


        this.__quessTrainingCartMutation =
          String(
            method || "GET"
          ).toUpperCase() !== "GET" &&
          CART_MUTATION_PATTERN.test(
            pathname
          );
      }


      catch (error) {
        this.__quessTrainingCartMutation =
          false;
      }


      return originalXhrOpen.call(
        this,
        method,
        url,
        ...remainingArguments
      );
    };


  XMLHttpRequest.prototype.send =
    function (
      body
    ) {
      if (
        this.__quessTrainingCartMutation
      ) {
        this.addEventListener(
          "loadend",

          function () {
            scheduleTrainingCartRefresh(
              80
            );


            window.setTimeout(
              function () {
                scheduleTrainingCartRefresh(
                  0
                );
              },
              500
            );
          },

          {
            once: true
          }
        );
      }


      return originalXhrSend.call(
        this,
        body
      );
    };


  /*
  Listen for common Shopify and theme
  cart-update events as an additional
  safety layer.
  */

  [
    "shopify:cart:lines-update",
    "cart:updated",
    "cart:refresh",
    "cart-drawer:refresh"
  ].forEach(
    function (
      eventName
    ) {
      document.addEventListener(
        eventName,

        function () {
          scheduleTrainingCartRefresh(
            80
          );
        }
      );
    }
  );


  window.addEventListener(
    "pageshow",

    function () {
      scheduleTrainingCartRefresh(
        0
      );
    }
  );


  /*
  Public API for the future registration
  modal and for manual testing.
  */

  window.QuessTrainingDetector = {
    refresh:
      refreshTrainingCartData,

    getState:
      function () {
        return (
          window.QuessTrainingCart ||
          null
        );
      }
  };


  function initializeTrainingCartDetector() {
    refreshTrainingCartData();
  }


  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initializeTrainingCartDetector,
      {
        once: true
      }
    );
  }


  else {
    initializeTrainingCartDetector();
  }
})();